import { useState, useEffect } from "react";
import Layout from "../../../components/Layout";
import BreadCrumb from "../../../components/BreadCrumb";
import PageTitle from "../../../components/PageTitle";
import { useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { httprequest } from "../../../data/api";
import { toast } from "react-toastify";
import PageLoader from "../../../components/PageLoader";
import NoData from "../../dashboard/components/NotFoundCard/NoData";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import nosearch from "../../../assets/images/no-search.svg";
import "../../enrolmentexam/admin/component/ResultDetail.css";

const CAResultDetail = () => {
  const { id } = useParams();
  const [filterTab, setFilterTab] = useState("All");
  const [questions, setQuestions] = useState([]);
  const [resultData, setResultData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const filteredQuestions = questions.filter(q => {
    if (filterTab === "Correct") return q.isCorrect;
    if (filterTab === "Incorrect") return !q.isCorrect;

    return true;
  });

  useEffect(() => {
    setLoading(true);
    httprequest.get(`/api/v1/submission-answers/ca_test/${id}`)
      .then(res => {
        if (res.data?.error) {
          setErrorMsg(res.data?.message || "CA submission not found");
          setQuestions([]);

          return;
        }

        const dataObj = res.data?.data || {};
        setResultData(dataObj);
        const payload = dataObj.questions_and_answers || dataObj.items || dataObj || res.data || [];

        const mappedQuestions = (Array.isArray(payload) ? payload : []).map((item, index) => {
          let parsedOptions = [];
          if (Array.isArray(item.options)) {
            parsedOptions = item.options;
          } else if (typeof item.options === "string") {
            try {
              parsedOptions = JSON.parse(item.options);
            } catch (e) {
              parsedOptions = item.options.split(",");
            }
          } else if (item.question?.options) {
            parsedOptions = item.question.options;
          }

          const qText = item.question_text || item.question?.question_text || item.question || `Question ${index + 1}`;
          const cAnswer = item.correct_answer_options?.[0] || item.correct_answer || item.question?.correct_answer || item.correctAnswer;
          const sAnswer = item.student_answer_options?.[0] || item.student_answer || item.selected_option || item.studentAnswer;

          const isCorrect = typeof item.is_correct !== "undefined"
            ? item.is_correct
            : (cAnswer && sAnswer && String(cAnswer).trim().toLowerCase() === String(sAnswer).trim().toLowerCase());

          return {
            id: item.id || index,
            question: qText,
            options: parsedOptions,
            correctAnswer: cAnswer,
            studentAnswer: sAnswer,
            isCorrect
          };
        });

        setQuestions(mappedQuestions);
        if (mappedQuestions.length === 0) {
          setErrorMsg("No questions found for this CA submission.");
        } else {
          setErrorMsg("");
        }
      })
      .catch(err => {
        setErrorMsg(err?.response?.data?.message || "CA submission not found");
        toast.error(err?.response?.data?.message || "Failed to load CA results.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const handleExportPDF = () => {
    if (!resultData || questions.length === 0) return;
    // eslint-disable-next-line new-cap
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text("CA Result Details", 14, 20);

    doc.setFontSize(12);
    doc.text(`Student Name: ${resultData?.student?.first_name || ""} ${resultData?.student?.last_name || ""}`, 14, 30);
    doc.text(`Score: ${resultData?.score || 0} / ${resultData?.max_score || 0}`, 14, 38);
    doc.text(`Class: ${resultData?.class?.name || "N/A"}`, 14, 46);
    doc.text(`Subject: ${resultData?.subject?.name || "N/A"}`, 14, 54);

    const tableBody = filteredQuestions.map((q, i) => {
      const optionsText = q.options?.map((opt) => `${opt}`).join("\n") || "N/A";

      return [
        `${i + 1}. ${q.question}`,
        optionsText,
        q.studentAnswer || "N/A",
        q.correctAnswer || "N/A",
        q.isCorrect ? "Correct" : "Incorrect"
      ];
    });

    autoTable(doc, {
      startY: 65,
      head: [["Question", "Options", "Student Answer", "Correct Answer", "Status"]],
      body: tableBody,
      headStyles: { fillColor: [0, 101, 255] },
      styles: { cellPadding: 4, fontSize: 10, cellWidth: "wrap" },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 50 },
        2: { cellWidth: 30 },
        3: { cellWidth: 30 },
        4: { cellWidth: 20 }
      }
    });

    doc.save(`CA_Result_${resultData?.student?.last_name || "Student"}.pdf`);
  };

  return (
    <Layout>
      <div className="breadcrumb-container">
        <div>
          <BreadCrumb
            link_one="/assessment-results"
            link_one_name="CA Test Results"
            active="CA Result Details"
            description="See the details of this CA result"
          />
        </div>
      </div>
      <div className="page-content">
        <PageTitle
          title="CA Result Details"
          subtitle="Detailed view of the student's CA result"
        />
        <div className="result-detail-container" style={{ marginTop: "20px" }}>

          {loading ? (
            <PageLoader />
          ) : errorMsg || questions.length === 0 ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", padding: "40px 0" }}>
              <NoData
                img={nosearch}
                title={errorMsg || "CA submission not found"}
                subtitle="We couldn't retrieve the details for this CA submission. It may not exist or has been removed."
              />
            </div>
          ) : (
            <>
              {/* Meta Header */}
              {resultData && (
                <div style={{ padding: "20px", background: "#0065ff", borderRadius: "8px", marginBottom: "20px", border: "none", display: "flex", gap: "20px", flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 min-content", minWidth: "120px" }}>
                    <p style={{ margin: 0, fontSize: "12px", color: "whitesmoke", opacity: 0.8, fontWeight: 600, textTransform: "uppercase" }}>Student Name</p>
                    <p style={{ margin: "4px 0 0", fontSize: "16px", color: "whitesmoke", fontWeight: 500 }}>
                      {resultData.student?.last_name || ""} {resultData.student?.first_name || ""}
                    </p>
                  </div>
                  <div style={{ width: "1px", background: "rgba(255, 255, 255, 0.3)" }}></div>
                  <div style={{ flex: "1 1 min-content", minWidth: "80px" }}>
                    <p style={{ margin: 0, fontSize: "12px", color: "whitesmoke", opacity: 0.8, fontWeight: 600, textTransform: "uppercase" }}>Score</p>
                    <p style={{ margin: "4px 0 0", fontSize: "16px", color: "whitesmoke", fontWeight: 500 }}>
                      {resultData.score || 0} / {resultData.max_score || 0}
                    </p>
                  </div>
                  <div style={{ width: "1px", background: "rgba(255, 255, 255, 0.3)" }}></div>
                  <div style={{ flex: "1 1 min-content", minWidth: "80px" }}>
                    <p style={{ margin: 0, fontSize: "12px", color: "whitesmoke", opacity: 0.8, fontWeight: 600, textTransform: "uppercase" }}>Class</p>
                    <p style={{ margin: "4px 0 0", fontSize: "16px", color: "whitesmoke", fontWeight: 500 }}>
                      {resultData.class?.name || "N/A"}
                    </p>
                  </div>
                  <div style={{ width: "1px", background: "rgba(255, 255, 255, 0.3)" }}></div>
                  <div style={{ flex: "1 1 min-content", minWidth: "120px" }}>
                    <p style={{ margin: 0, fontSize: "12px", color: "whitesmoke", opacity: 0.8, fontWeight: 600, textTransform: "uppercase" }}>Subject</p>
                    <p style={{ margin: "4px 0 0", fontSize: "16px", color: "whitesmoke", fontWeight: 500 }}>
                      {resultData.subject?.name || "N/A"}
                    </p>
                  </div>
                </div>
              )}

              {/* Filter Bar */}
              <div className="result-filter-bar">
                <div className="result-tabs">
                  <button
                    className={`result-tab ${filterTab === "All" ? "active" : ""}`}
                    onClick={() => setFilterTab("All")}
                  >
                    All
                  </button>
                  <button
                    className={`result-tab ${filterTab === "Correct" ? "active" : ""}`}
                    onClick={() => setFilterTab("Correct")}
                  >
                    Correct
                  </button>
                  <button
                    className={`result-tab ${filterTab === "Incorrect" ? "active" : ""}`}
                    onClick={() => setFilterTab("Incorrect")}
                  >
                    Incorrect
                  </button>
                </div>

                <button className="result-export-btn" onClick={handleExportPDF}>
                  <Icon icon="ph:download-simple" />
                  Export PDF
                </button>
              </div>

              {/* Main Layout */}
              <div className="result-content-layout">

                {/* Sidebar Question Nav */}
                <div className="result-sidebar">
                  <div className="result-sidebar-title">QUESTIONS</div>
                  <div className="result-question-nav">
                    {questions.map((q, index) => (
                      <div
                        key={q.id}
                        className={`result-nav-box ${q.isCorrect ? "correct" : "incorrect"}`}
                      >
                        {index + 1}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Questions List */}
                <div className="result-questions-list">
                  {filteredQuestions.map((q, index) => (
                    <div key={q.id} className={`result-question-card ${q.isCorrect ? "correct" : "incorrect"}`}>
                      <div className="result-question-title">
                        {index + 1}. {q.question}
                      </div>
                      <div className={`result-badge ${q.isCorrect ? "correct" : "incorrect"}`}>
                        {q.isCorrect ? "Correct" : "Incorrect"}
                      </div>

                      <div className="result-options">
                        {q.options && q.options.map((opt, i) => {
                          // Determine option state
                          const isCorrectAnswer = String(opt) === String(q.correctAnswer);
                          const isStudentAnswer = String(opt) === String(q.studentAnswer);

                          let optionClass = "";
                          let radioClass = "";
                          if (isCorrectAnswer) {
                            optionClass = "correct";
                            radioClass = "checked correct";
                          } else if (isStudentAnswer && !q.isCorrect) {
                            optionClass = "incorrect";
                            radioClass = "checked incorrect";
                          } else if (isStudentAnswer && q.isCorrect) {
                            optionClass = "correct";
                            radioClass = "checked correct";
                          }

                          return (
                            <div key={i} className={`result-option ${optionClass}`}>
                              <div className={`result-radio ${radioClass}`} />
                              <span>{opt?.option || opt}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            </>
          )}

        </div>
      </div>
    </Layout>
  );
};

export default CAResultDetail;
